import { useCallback, useId, useLayoutEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Button from '@/Components/Marketing/Button';
import DissolvingWindow from '@/Components/Marketing/DissolvingWindow';
import LazyBoundary from '@/Components/Marketing/LazyBoundary';
import { demoHref } from '@/Components/Marketing/links';
import Reveal from '@/Components/Marketing/Reveal';
import Section from '@/Components/Marketing/Section';
import { CornerCloud } from '@/Components/Marketing/Texture';
import { Heading } from '@/Components/Marketing/Typography';
import useMediaQuery from '@/Components/Marketing/useMediaQuery';
import useNearViewport from '@/Components/Marketing/useNearViewport';
import PlaybackGate from '@/Components/Marketing/Vignettes/PlaybackGate';
import { cross, keyFamily } from '@/lib/keyFamily';
import { cn } from '@/lib/utils';

// The cells' vignettes sit below the fold and are loaded as they near the
// viewport: one small chunk first (it maps a cell to its vignette), then each
// vignette's own. One loader per cell, so each is loaded (and retried) alone.
const loadBentoVignettes = () => import('@/Components/Marketing/Welcome/BentoVignette');

// Where a pointer can hover (the `fine:` variant's query), a cell plays only
// while it is hovered: at rest otherwise, and a pass under way when the
// pointer leaves runs to its end (PlaybackGate). Touch screens, with no
// hover, play each cell while it is on screen, as before (2026-10-04).
export const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

// Each tint is the cell's fill and the colour of its hairline. No orange in
// the cells themselves (R87, R2-5): they stay neutral, the large one and Lab
// sunken, the rest white; the orange is the cloud behind them (2026-10-13).
const TINTS = {
    sunken: 'bg-mk-sunken ring-mk-ink/5',
    white: 'bg-mk-surface ring-mk-line',
};

// Desktop: a 6-column grid in two blocks. The modelling cell takes 4 columns
// and both rows beside the two supplier/import cells; below, publish and Lab
// (half the grid each, with a taller window: their vignettes glide down
// through their story) stand one over the other beside Synex Consul (L4),
// whose cell takes the other half and both their rows: a panel is tall and
// narrow, and on a row of its own across the grid it left half the row
// blank (2026-10-24). Tablet: modelling across both columns, suppliers and
// import in a pair, then publish over Lab beside Consul. Phone: one column.
const CELLS = [
    { key: 'model', tint: 'sunken', span: 'md:col-span-2 lg:col-span-4 lg:row-span-2', large: true },
    { key: 'suppliers', tint: 'white', span: 'lg:col-span-2' },
    { key: 'import', tint: 'white', span: 'lg:col-span-2' },
    { key: 'publish', tint: 'white', span: 'lg:col-span-3', tall: true },
    { key: 'lab', tint: 'sunken', span: 'lg:col-span-3', tall: true },
    { key: 'consul', tint: 'white', span: 'md:col-start-2 md:row-start-3 md:row-span-2 lg:col-start-4 lg:col-span-3', panel: true },
];

/** Each cell's title and text. */
export const BENTO_COPY = keyFamily('site.home.bento.{0}.{1}', cross(CELLS.map(({ key }) => key), ['title', 'text']));

// A panel cell's window (Consul's): on a phone tall enough for the panel with
// its release card, from md up as tall as its two rows leave above the copy.
const PANEL_BOX = 'h-[36rem] sm:h-[42rem] md:h-auto md:flex-1';

// The panel's frame ends where its window has dissolved: the plate runs 16px
// past the cell's edge (DissolvingWindow), and the panel stops short of that.
const PANEL_FRAME = 'w-[calc(100%-16px)]';

// What the panel keeps past its last control, in layout px: the 12px around
// its composer, and as much right of the header's buttons.
const PANEL_PADDING = 12;

// The plate starts this far into the window's box (DissolvingWindow).
const PLATE_INSET = 12;

/**
 * The height, in layout px, a panel cell's vignette is laid out at for its
 * window's `box` ({ width, height }, the window's own box), so the whole
 * panel shows: it is laid out at the plate's visible width (fitted down to
 * it from `baseWidth`, never drawn past 1:1, VignetteFrame) and as tall as
 * puts its foot, all but its padding, where the window starts to dissolve
 * (`fadeY`, the window's own). Only the padding runs into the dissolve.
 * Undefined for a box not laid out: the vignette keeps its own height.
 */
export function fitHeight(box, { fadeY, baseWidth }) {
    const width = box.width - PLATE_INSET;
    const height = box.height - PLATE_INSET;
    if (width <= 0 || height <= 0) return undefined;
    const factor = Math.min(1, width / baseWidth);

    return Math.floor((height * fadeY) / 100 / factor) + PANEL_PADDING;
}

/**
 * A panel cell's fitted height (fitHeight) for the window `node`, measured
 * before paint and again as the window is resized; `ready` once measured.
 * A cell that is not a panel, or a browser that cannot measure, is ready at
 * once with no height of its own.
 */
function useFittedHeight(node, layout, fit) {
    const [fitted, setFitted] = useState({ ready: !fit, height: undefined });
    const fadeY = layout.fade?.[1];
    const baseWidth = layout.vignette?.baseWidth;

    useLayoutEffect(() => {
        if (!fit || !node) return undefined;
        const measure = () => {
            const height = fitHeight({ width: node.clientWidth, height: node.clientHeight }, { fadeY, baseWidth });
            setFitted((current) => (current.ready && current.height === height ? current : { ready: true, height }));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(node);

        return () => observer.disconnect();
    }, [node, fit, fadeY, baseWidth]);

    return fitted;
}

/**
 * What each vignette is laid out as in its cell, from the grid above (the
 * modelling cell is wide from md up; below md it is a phone column), and
 * where its window starts to dissolve. `readable` keeps a vignette's content
 * to that share of its width, inside the part of the window drawn whole.
 * Every width is a desktop one, a phone column's too (drawn smaller there):
 * a stage lays the app out at desktop breakpoints, which a narrower layout
 * would crowd (VignetteFrame).
 */
function useCellLayouts() {
    const lg = useMediaQuery('(min-width: 1024px)');
    const xl = useMediaQuery('(min-width: 1280px)');

    return {
        // The large cell dissolves later. From lg to xl it is narrower but
        // just as tall: drawn larger, the list fills more of it.
        model: { fade: [78, 66], vignette: { baseWidth: lg && !xl ? 760 : 880, readable: 0.8 } },
        suppliers: { vignette: { readable: 0.75 } },
        // Wide enough for the run's Created, Completed and Run time on one line.
        import: { vignette: { readable: 0.85 } },
        publish: { vignette: { readable: 0.75 } },
        // Laid out wider and dissolving later: the bulk edit's preview, as
        // wide as the editor and its after column included, stays where the
        // window is drawn whole.
        lab: { fade: [84, 60], vignette: { baseWidth: 640, readable: 0.82 } },
        // The panel at the app's own width, fitted to its window (fitHeight)
        // and drawn whole: the window dissolves only past its padding.
        consul: { fade: [98, 90], vignette: { baseWidth: 440, className: PANEL_FRAME } },
    };
}

/** Each cell's vignette, by the cell's key (see BentoVignette's loadVignette). */
const VIGNETTE_LOADERS = Object.fromEntries(CELLS.map(({ key }) => [
    key,
    () => loadBentoVignettes().then((module) => module.loadVignette(key)),
]));

/**
 * A cell's window onto the app (a DissolvingWindow), whose vignette loads as
 * the cell nears the viewport. The window's white plate is the placeholder:
 * it has its final size (and dissolves the same way) before the chunk
 * arrives, so nothing moves when it does; if the chunk fails, the plate
 * stays (LazyBoundary). A panel cell's vignette is fitted to the window
 * (useFittedHeight), measured before it loads.
 */
function CellWindow({ cell, layout }) {
    const [nearRef, near] = useNearViewport();
    const [node, setNode] = useState(null);
    const ref = useCallback((element) => {
        nearRef(element);
        setNode(element);
    }, [nearRef]);
    const fitted = useFittedHeight(node, layout, Boolean(cell.panel));
    const [fadeX, fadeY] = layout.fade ?? [];

    return (
        <DissolvingWindow ref={ref} data-vignette-window={cell.key} fadeX={fadeX} fadeY={fadeY}>
            {near && fitted.ready && (
                <LazyBoundary load={VIGNETTE_LOADERS[cell.key]}>
                    {(Vignette) => <Vignette {...layout.vignette} height={fitted.height} />}
                </LazyBoundary>
            )}
        </DissolvingWindow>
    );
}

/**
 * The platform in six cells, the app on top: modelling, supplier requests,
 * bulk import, the public QR page, Lab and Synex Consul (the panel asked to
 * release a batch, drawn whole beside publish and Lab). Each cell shows a live
 * vignette of that part of the product in a window inset from the top left
 * that dissolves into the cell before its right and bottom edges, played
 * while the cell is hovered (HOVER_QUERY). The hero's orange cloud lights the
 * section faintly from its top right corner (Texture's CornerCloud).
 */
export default function PlatformBento() {
    const { formatMessage } = useIntl();
    const t = id => formatMessage({ id });
    const headingId = useId();
    const layouts = useCellLayouts();
    const canHover = useMediaQuery(HOVER_QUERY);
    const [hovered, setHovered] = useState(null);

    return (
        <Section
            tone="surface"
            aria-labelledby={headingId}
            className="relative isolate overflow-hidden border-t border-mk-line"
            backdrop={<CornerCloud corner="top-right" />}
        >
            <Reveal>
                <Heading id={headingId} className="max-w-3xl">
                    {t('site.home.bento.title')}
                </Heading>
            </Reveal>

            <div className="relative mt-12 sm:mt-16">
                <ul role="list" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
                    {CELLS.map((cell, index) => (
                        <Reveal as="li" key={cell.key} delay={(index % 3) * 0.06} className={cn('flex', cell.span)}>
                            {/* One even hairline all round, and a light top edge. */}
                            <article
                                data-bento-cell={cell.key}
                                onPointerEnter={(event) => event.pointerType !== 'touch' && setHovered(cell.key)}
                                onPointerLeave={() => setHovered((current) => (current === cell.key ? null : current))}
                                className={cn(
                                    'flex w-full flex-col overflow-hidden rounded-mk-card ring-1 ring-inset',
                                    'shadow-[inset_0_1px_0_rgb(255_255_255/0.8)]',
                                    TINTS[cell.tint],
                                )}
                            >
                                <div
                                    className={cn(
                                        'relative',
                                        cell.panel ? PANEL_BOX : 'h-56 sm:h-64',
                                        cell.large && 'sm:h-80 lg:h-auto lg:min-h-[24rem] lg:flex-1',
                                        cell.tall && 'lg:h-80',
                                    )}
                                >
                                    <PlaybackGate open={!canHover || hovered === cell.key}>
                                        <CellWindow cell={cell} layout={layouts[cell.key]} />
                                    </PlaybackGate>
                                </div>
                                <div className="px-6 pb-7 pt-5 sm:px-8 sm:pb-8">
                                    <h3
                                        className={cn(
                                            'text-lg font-semibold tracking-[-0.02em] text-mk-ink',
                                            cell.large && 'sm:text-xl',
                                        )}
                                    >
                                        {t(BENTO_COPY.key(cell.key, 'title'))}
                                    </h3>
                                    <p className="mt-2 max-w-[48ch] text-[15px] leading-relaxed text-pretty text-mk-body">
                                        {t(BENTO_COPY.key(cell.key, 'text'))}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </ul>
            </div>

            {/* The way on, under the cells and aligned to their edges: the
                line, the platform page, a demo. On the section's own white,
                with no fill, ring or corners: drawn as a sunken row at a
                cell's size, it read as a cell whose vignette never came
                (2026-10-05), and a text link alone there was missed. */}
            <Reveal className="mt-10 sm:mt-12">
                <div
                    data-bento-cta=""
                    className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10"
                >
                    <p className="max-w-[36ch] text-xl font-semibold leading-snug tracking-[-0.02em] text-balance text-mk-ink sm:text-2xl">
                        {t('site.home.bento.cta')}
                    </p>
                    <div className="flex shrink-0 flex-wrap gap-3">
                        <Button href={route('outer.platform')} size="lg">
                            {t('site.home.bento.link')}
                        </Button>
                        <Button href={demoHref()} variant="secondary" size="lg">
                            {t('site.cta.demo')}
                        </Button>
                    </div>
                </div>
            </Reveal>
        </Section>
    );
}
